import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, HostListener, inject, OnInit, signal, ViewChild, DOCUMENT } from '@angular/core';
import { NavigationEnd, Params, Router, RouterModule } from '@angular/router';
import { filter, startWith } from 'rxjs';
import { defaultPrefs, Tech } from '../../../core/models/user.model';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { ConversionContextService } from '../../../core/services/conversion-context.service';
import { AuthService } from '../../../core/services/auth.service';
import { isProActive } from '../../../core/utils/entitlements.util';
import { PREPARE_GROUPS, PrepareGroup, PrepareItem, TargetName } from '../../prepare/prepare.registry';
import { PREP_MENU_SECTIONS, isPrepMenuPath } from '../../../core/content/prep-menu';
import { PrepMenuPanelComponent, PrepMenuSelection, prepMenuPanelCenter } from '../prep-menu/prep-menu-panel.component';
import { AppSidebarDrawerService } from '../../../core/services/app-sidebar-drawer.service';

type Mode =
  | 'dashboard'
  | 'tech-list'
  | 'tech-detail'
  | 'sd-list'
  | 'sd-detail'
  | 'course'
  | 'profile'
  | 'not-found';

type VisibleEntry =
  | { origin: string; kind: 'search'; item: PrepareItem; link: any[]; group?: string }
  | { origin: 'continue'; kind: 'continue'; link: any[] }
  | { origin: string; kind: 'prep'; link: any[]; selection: PrepMenuSelection };

type ContinueAction = {
  title: string;
  subtitle: string;
  route: any[];
  badge: string | null;
  item: PrepareItem | null;
};

@Component({
    selector: 'app-header',
    imports: [CommonModule, RouterModule, PrepMenuPanelComponent],
    styleUrls: ['./header.component.css'],
    templateUrl: './header.component.html'
})
export class HeaderComponent implements OnInit {
  @ViewChild('desktopStudyTrigger') desktopStudyTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('mobileStudyTrigger') mobileStudyTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('studySearchInput') studySearchInput?: ElementRef<HTMLInputElement>;

  private doc = inject(DOCUMENT);
  private router = inject(Router);
  private hostEl = inject(ElementRef<HTMLElement>);
  private drawerState = inject(AppSidebarDrawerService);
  private analytics = inject(AnalyticsService);
  private readonly conversionContext = inject(ConversionContextService);

  // route state
  mode = signal<Mode>('dashboard');
  currentPath = signal('/');
  currentTech = signal<'javascript' | 'angular' | 'react' | 'vue' | 'html' | 'css' | null>(null);
  section = signal<'coding' | 'trivia' | 'debug' | null>(null);

  // prepare groups
  groups: PrepareGroup[] = PREPARE_GROUPS.filter(g => g.key !== 'courses');
  private recentKey = 'fa:recent:study';
  recentItems = signal<PrepareItem[]>([]);
  searchTerm = '';
  activeIndex = signal<number>(-1);
  browseAllOpen = signal(false);
  expanded = signal<Record<string, boolean>>({ foundations: true, practice: true });

  // menus
  megaOpen = signal(false);
  megaAnchorX = signal<number | null>(null);
  profileOpen = signal(false);
  sidebarDrawerOpen = this.drawerState.isOpen;

  topPicks(): PrepareItem[] {
    if (this.isSearching()) return [];
    const picks: PrepareItem[] = [];
    for (const g of this.groups) {
      for (const it of g.items) {
        picks.push(it);
        if (picks.length >= 8) return picks;
      }
    }
    return picks;
  }

  limitedGroups(): PrepareGroup[] {
    return this.groups.map(g => ({ ...g, items: g.items.slice(0, 2) }));
  }

  public auth = inject(AuthService);
  isPro = computed(() => isProActive(this.auth.user()));
  brandLink = computed(() => this.auth.authUiState() === 'authenticated' ? ['/dashboard'] : ['/']);
  brandDestination = computed(() => this.auth.authUiState() === 'authenticated' ? '/dashboard' : '/');
  ctaLabel = computed(() => {
    if (this.isPro()) return 'Manage subscription';
    return this.auth.isLoggedIn() ? 'Upgrade' : 'Start prep';
  });
  ctaLink = computed(() => {
    if (this.isPro()) return ['/profile'];
    return this.auth.isLoggedIn() ? ['/pricing'] : ['/guides', 'interview-blueprint', 'intro'];
  });
  ctaDestination = computed(() => {
    if (this.isPro()) return '/profile';
    return this.auth.isLoggedIn() ? '/pricing' : '/guides/interview-blueprint/intro';
  });
  isInterviewQuestionsActive = computed(() => this.currentPath() === '/interview-questions');
  isStudyActive = computed(() => {
    const path = this.currentPath();
    if (
      path === '/'
      || path === '/dashboard'
      || path.startsWith('/profile')
      || path.startsWith('/admin')
      || path.startsWith('/pricing')
      || path.startsWith('/billing')
      || path.startsWith('/auth')
      || path.startsWith('/legal')
      || path.startsWith('/404')
      || path.startsWith('/changelog')
    ) {
      return false;
    }

    if (isPrepMenuPath(path)) return true;

    if (
      path.startsWith('/interview-questions')
      || path.startsWith('/coding')
      || path.startsWith('/incidents')
      || path.startsWith('/tradeoffs')
      || path.startsWith('/tracks')
      || path.startsWith('/companies')
      || path.startsWith('/focus-areas')
      || path.startsWith('/guides')
      || path.startsWith('/system-design')
    ) {
      return true;
    }

    return /^\/(javascript|angular|react|vue|html|css)(\/|$)/.test(path);
  });

  readonly prepSection = signal<string>(PREP_MENU_SECTIONS[0]?.id ?? '');

  readonly continueAction = computed<ContinueAction>(() => {
    const recent = this.recentItems()[0] ?? null;
    const recentLink = recent ? this.intentToLink(recent) : null;
    const loggedIn = this.auth.isLoggedIn();
    return {
      title: 'Continue where I left off',
      subtitle: recent
        ? recent.subtitle
        : (loggedIn
          ? 'Return to your dashboard or your last interview prep path.'
          : 'Open the fastest route back into interview prep.'),
      route: recentLink ?? (loggedIn ? ['/dashboard'] : ['/guides', 'interview-blueprint', 'intro']),
      badge: recent ? 'Recent' : null,
      item: recent,
    };
  });

  constructor() {
    this.router.events.pipe(filter(e => e instanceof NavigationEnd), startWith(null))
      .subscribe(() => {
        this.parseUrl(this.router.url);
        this.closeAll();
        this.drawerState.close();
        this.updateSafeTop();
        this.loadRecents();
      });

  }

  isAdmin(): boolean {
    return (this.auth.user()?.role ?? 'user') === 'admin';
  }

  ngOnInit(): void { }

  // —— utils / routing ——
  isSearching(): boolean {
    return (this.searchTerm || '').trim().length > 0;
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.searchTerm = target?.value ?? '';
  }

  private resolveTech(): Tech {
    const pref = (defaultPrefs().defaultTech as Tech | undefined) ?? 'javascript';
    return this.currentTech() ?? pref;
  }

  isSystemDesign = computed(() =>
    this.currentTech() === null && (this.mode() === 'sd-list' || this.mode() === 'sd-detail')
  );
  isDetailPage = computed(() => this.mode() === 'tech-detail' || this.mode() === 'sd-detail');
  showContextStrip = computed(() => false);
  showTechTabs = computed(() => false);
  showPrepareTrigger = computed(() =>
    this.mode() === 'dashboard' || this.mode() === 'tech-detail' || this.mode() === 'profile'
  );

  visibleItems(): VisibleEntry[] {
    if (this.isSearching()) {
      const out: VisibleEntry[] = [];
      this.searchResults().forEach((it, idx) => {
        const link = this.intentToLink(it);
        if (!link || it.disabled) return;
        out.push({ origin: `search-${idx}`, kind: 'search', item: it, link, group: it._group });
      });
      return out;
    }

    const section = PREP_MENU_SECTIONS.find((entry) => entry.id === this.prepSection()) ?? PREP_MENU_SECTIONS[0];
    const out: VisibleEntry[] = [{ origin: 'continue', kind: 'continue', link: this.continueAction().route }];
    for (const item of section?.items ?? []) {
      out.push({
        origin: `prep-${item.key}`,
        kind: 'prep',
        link: [item.route],
        selection: { key: item.key, chip: null, section: section.id, route: item.route },
      });
    }
    return out;
  }

  highlightKey(): string | null {
    if (this.isSearching()) return null;
    const entry = this.visibleItems()[this.activeIndex()];
    return entry?.kind === 'prep' ? entry.selection.key : null;
  }

  rowIndex(origin: string): number {
    return this.visibleItems().findIndex(v => v.origin === origin);
  }

  private parseUrl(url: string) {
    const cleanPath = url.split('?')[0].split('#')[0] || '/';
    this.currentPath.set(cleanPath === '' ? '/' : cleanPath);
    const segs = cleanPath.split('/').filter(Boolean);

    this.mode.set('dashboard');
    this.currentTech.set(null);
    this.section.set(null);

    if (segs.length === 0) { this.mode.set('dashboard'); return; }
    if (segs[0] === '404') { this.mode.set('not-found'); return; }
    if (segs[0] === 'courses') { this.mode.set('course'); return; }
    if (segs[0] === 'system-design') { this.mode.set(segs.length === 1 ? 'sd-list' : 'sd-detail'); return; }
    if (segs[0] === 'profile') { this.mode.set('profile'); return; }

    const tech = segs[0] as 'javascript' | 'angular' | 'react' | 'vue' | 'html' | 'css';
    const isTech =
      tech === 'javascript' || tech === 'angular' || tech === 'react' ||
      tech === 'vue' || tech === 'html' || tech === 'css';
    if (isTech) this.currentTech.set(tech);

    if (!isTech) return;

    if (segs.length === 1) { this.mode.set('tech-list'); this.section.set('coding'); return; }

    const sec = segs[1] as 'coding' | 'trivia' | 'debug';
    if (sec === 'coding' || sec === 'trivia' || sec === 'debug') {
      this.section.set(sec);
      this.mode.set(segs.length === 2 ? 'tech-list' : 'tech-detail');
    }
  }

  trackByGroupKey(_: number, g: PrepareGroup) { return g.key; }
  trackByItemKey(_: number, it: PrepareItem) { return it.key; }

  toggleBrowseAll() {
    this.browseAllOpen.update(v => !v);
  }

  toggleGroup(key: string) {
    this.expanded.update(prev => ({ ...prev, [key]: !prev[key] }));
  }

  isGroupExpanded(key: string) {
    return !!this.expanded()[key];
  }

  viewAllLink(g: PrepareGroup): any[] | null {
    switch (g.key) {
      case 'foundations':
      case 'resources':
        return ['/guides'];
      case 'practice':
        return ['/coding'];
      case 'system':
        return ['/system-design'];
      case 'companies':
        return ['/companies'];
      default:
        return null;
    }
  }

  searchResults(): (PrepareItem & { _group?: string })[] {
    if (!this.isSearching()) return [];
    const term = this.searchTerm.trim().toLowerCase();
    const all: (PrepareItem & { _group?: string })[] = [];
    this.groups.forEach(g => all.push(...g.items.map(it => ({ ...it, _group: g.title }))));
    const match = (it: PrepareItem & { _group?: string }) => {
      const hay = [it.title, it.subtitle, it.badge, it._group].filter(Boolean).map(v => String(v).toLowerCase()).join(' ');
      return hay.includes(term);
    };
    return all.filter(match).slice(0, 20);
  }

  intentToLink(it: PrepareItem): any[] | null {
    if (it.intent !== 'route' || !it.target) return null;
    const t = it.target;
    switch (t.name as TargetName) {
      case 'practice': {
        const tech = this.resolveTech();
        return ['/', tech];
      }
      case 'system': {
        const section = t.params?.['section'] as string | undefined;
        if (section === 'guide') return ['/guides', 'system-design-blueprint'];
        return ['/system-design'];
      }
      case 'companies': {
        const c = t.params?.['company'] as string | undefined;
        return c ? ['/companies', c] : ['/companies'];
      }
      case 'courses':
        return ['/courses'];
      case 'tracks':
        return ['/tracks'];
      case 'warmup':
        return ['/interview-questions'];
      case 'tools': {
        const tool = String(t.params?.['tool'] ?? 'cv').trim();
        return ['/tools', tool || 'cv'];
      }
      case 'guides': {
        const section = (t.params?.['section'] as string | undefined) ?? '';
        if (section === 'playbook' || section === 'interview-blueprint') return (['/guides', 'interview-blueprint']);
        if (section === 'framework-prep') return (['/guides', 'framework-prep']);
        if (section === 'behavioral') return (['/guides', 'behavioral']);
        if (section === 'system-design' || section === 'system-design-blueprint') return (['/guides', 'system-design-blueprint']);
        return ['/guides'];
      }
      default:
        return null;
    }
  }

  private updateSafeTop() {
    const base = 56;
    const ctx = this.showContextStrip() ? 48 : 0;
    this.doc.documentElement.style.setProperty('--app-safe-top', `${base + ctx}px`);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target;
    if (!(target instanceof Node)) {
      this.closeAll();
      return;
    }
    if (!this.hostEl.nativeElement.contains(target)) {
      this.closeAll();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onDocumentEsc(event: Event) {
    if (!this.megaOpen()) {
      this.closeAll();
      return;
    }

    event.preventDefault();
    this.closeMegaAndRestoreFocus();
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKey(ev: KeyboardEvent) {
    const target = ev.target as HTMLElement | null;
    const inInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
    const key = ev.key.toLowerCase();
    if ((ev.ctrlKey || ev.metaKey) && key === 'k') {
      ev.preventDefault();
      if (this.megaOpen()) {
        this.closeMegaAndRestoreFocus();
      } else {
        this.lastStudyTrigger = this.findVisibleStudyTrigger();
        this.openOnly('mega');
        this.trackTopNavClick('primary', 'study');
        this.analytics.track('header_study_opened', { trigger: 'shortcut' });
      }
      return;
    }
    if (!inInput && key === '/') {
      ev.preventDefault();
      this.lastStudyTrigger = this.findVisibleStudyTrigger();
      this.openOnly('mega');
      this.analytics.track('header_study_opened', { trigger: 'shortcut' });
      this.activeIndex.set(0);
    }
  }

  private openOnly(which: 'mega' | 'profile' | null) {
    this.megaOpen.set(which === 'mega');
    this.profileOpen.set(which === 'profile');
    if (which === 'mega') {
      this.syncMegaAnchor();
      this.prepSection.set(PREP_MENU_SECTIONS[0]?.id ?? '');
      this.searchTerm = '';
      this.activeIndex.set(-1);
      this.browseAllOpen.set(false);
      this.loadRecents();
      this.focusStudySearchAfterRender();
    } else {
      this.megaAnchorX.set(null);
      this.searchTerm = '';
    }
  }

  toggleMega(ev?: Event) {
    this.drawerState.close();
    if (this.megaOpen()) {
      this.openOnly(null);
      return;
    }
    const trigger = ev?.currentTarget as HTMLElement | null | undefined;
    this.lastStudyTrigger = trigger?.tagName === 'BUTTON'
      ? trigger as HTMLButtonElement
      : this.findVisibleStudyTrigger();
    this.openOnly('mega');
    this.trackTopNavClick('primary', 'study');
    this.analytics.track('header_study_opened', { trigger: ev ? 'button' : 'shortcut' });
  }
  toggleProfileMenu(event?: Event) {
    event?.stopPropagation();
    this.drawerState.close();
    if (!this.profileOpen()) {
      this.trackTopNavClick('utility', 'account_menu');
    }
    this.openOnly(this.profileOpen() ? null : 'profile');
  }
  toggleSidebarDrawer(event?: Event) {
    event?.stopPropagation();
    this.openOnly(null);
    if (!this.sidebarDrawerOpen()) {
      this.trackTopNavClick('mobile_menu', 'sidebar');
    }
    this.drawerState.toggle();
  }

  closeAll() { this.openOnly(null); }

  onSearchKeydown(event: KeyboardEvent) {
    if (event.isComposing) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        return;
      case 'Enter':
        if (this.activeIndex() < 0) return;
        event.preventDefault();
        this.activateActive();
        return;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.closeMegaAndRestoreFocus();
        return;
      default:
        return;
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.megaOpen()) this.syncMegaAnchor();
  }

  logout() {
    this.auth.logout().subscribe();
    this.closeAll();
    this.router.navigate(['/']);
  }

  onPrepItemSelected(selection: PrepMenuSelection) {
    const params = {
      route: selection.route,
      section: selection.section,
      surface: 'app',
      ...(selection.chip ? { chip: selection.chip } : {}),
    };
    if (selection.key === 'question_library' && !selection.chip) {
      this.analytics.track('header_study_browse_full_library_clicked', { destination: 'question_library', ...params });
    } else {
      this.analytics.track('header_study_primary_cta_clicked', { action: selection.key, ...params });
    }
    this.closeAll();
  }

  onContinueClick() {
    const action = this.continueAction();
    if (action.item) this.pushRecent(action.item);
    this.analytics.track('header_study_primary_cta_clicked', {
      action: 'continue',
      route: this.serializeLink(action.route),
      surface: 'app',
    });
    this.closeAll();
  }

  trackBrandClick() {
    this.analytics.track('header_brand_clicked', {
      surface: 'app',
      destination: this.brandDestination(),
      auth_state: this.authState(),
    });
  }

  trackUtilityClick(destination: string) {
    this.trackTopNavClick('utility', destination);
  }

  onInterviewQuestionsClick() {
    this.trackTopNavClick('primary', '/interview-questions');
    this.closeAll();
  }

  // Signed-out visitors get a plain Pricing link next to the account menu, as on
  // the homepage header; signed-in users already have Upgrade or Manage subscription.
  onPricingClick() {
    this.conversionContext.rememberPricingContext('app_header', 'app_header_utility');
    this.trackTopNavClick('utility', '/pricing');
    this.closeAll();
  }

  onItemNavigate(it: PrepareItem) {
    this.pushRecent(it);
    this.closeAll();
  }

  onViewAll() {
    this.closeAll();
  }

  moveActive(delta: number) {
    const list = this.visibleItems();
    if (!list.length) return;
    const next = (this.activeIndex() + delta + list.length) % list.length;
    this.activeIndex.set(next);
  }

  activateActive() {
    const list = this.visibleItems();
    const idx = this.activeIndex();
    if (idx < 0 || idx >= list.length) return;
    const entry = list[idx];
    if (entry.kind === 'search') {
      this.onItemNavigate(entry.item);
    } else if (entry.kind === 'continue') {
      this.onContinueClick();
    } else {
      this.onPrepItemSelected(entry.selection);
    }
    this.router.navigate(entry.link);
  }

  private loadRecents() {
    try {
      const raw = localStorage.getItem(this.recentKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const map = new Map(this.groups.flatMap(g => g.items.map(it => [it.key, it])));
        const items: PrepareItem[] = [];
        for (const k of parsed) {
          const it = map.get(k);
          if (it) items.push(it);
        }
        this.recentItems.set(items.slice(0, 5));
      }
    } catch { }
  }

  private pushRecent(it: PrepareItem) {
    const existing = this.recentItems().filter(r => r.key !== it.key);
    const next = [it, ...existing].slice(0, 5);
    this.recentItems.set(next);
    try { localStorage.setItem(this.recentKey, JSON.stringify(next.map(x => x.key))); } catch { }
  }

  private syncMegaAnchor(ev?: Event) {
    const viewportWidth = this.doc.defaultView?.innerWidth ?? 1280;
    const triggerCenter = this.readEventCenter(ev) ?? this.findVisibleStudyTriggerCenter();
    this.megaAnchorX.set(prepMenuPanelCenter(viewportWidth, triggerCenter));
  }

  private readEventCenter(ev?: Event): number | null {
    const target = ev?.currentTarget;
    if (!(target instanceof HTMLElement)) return null;
    const rect = target.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return rect.left + rect.width / 2;
  }

  private lastStudyTrigger: HTMLButtonElement | null = null;

  private findVisibleStudyTrigger(): HTMLButtonElement | null {
    const desktop = this.desktopStudyTrigger?.nativeElement;
    if (desktop && this.isVisible(desktop)) return desktop;

    const mobile = this.mobileStudyTrigger?.nativeElement;
    if (mobile && this.isVisible(mobile)) return mobile;

    return desktop ?? mobile ?? null;
  }

  private findVisibleStudyTriggerCenter(): number | null {
    const trigger = this.findVisibleStudyTrigger();
    if (!trigger) return null;
    const rect = trigger.getBoundingClientRect();
    return rect.left + rect.width / 2;
  }

  private focusStudySearchAfterRender() {
    this.doc.defaultView?.setTimeout(() => {
      if (this.megaOpen()) this.studySearchInput?.nativeElement.focus();
    }, 0);
  }

  private closeMegaAndRestoreFocus() {
    const trigger = this.lastStudyTrigger ?? this.findVisibleStudyTrigger();
    this.openOnly(null);
    trigger?.focus();
  }

  private isVisible(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    const style = this.doc.defaultView?.getComputedStyle(el);
    if (!style) return true;
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  private serializeLink(route: any[], queryParams?: Params): string {
    const path = route.map((segment) => String(segment || '')).join('/') || '/';
    if (!queryParams || !Object.keys(queryParams).length) return path;
    const query = new URLSearchParams(
      Object.entries(queryParams).reduce<Record<string, string>>((acc, [key, value]) => {
        if (value !== null && value !== undefined) acc[key] = String(value);
        return acc;
      }, {})
    ).toString();
    return query ? `${path}?${query}` : path;
  }

  private trackTopNavClick(area: 'primary' | 'utility' | 'mobile_menu', destination: string) {
    this.analytics.track('header_top_nav_clicked', {
      surface: 'app',
      area,
      destination,
      auth_state: this.authState(),
    });
  }

  private authState(): 'guest' | 'logged_in_free' | 'logged_in_pro' {
    if (!this.auth.isLoggedIn()) return 'guest';
    return this.isPro() ? 'logged_in_pro' : 'logged_in_free';
  }
}
